import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchCycles } from '../store/slices/cyclesSlice';
import { cyclesAPI } from '../services/api';
import { formatDate, downloadBlob } from '../utils/helpers';
import toast from 'react-hot-toast';

export default function IncrementCyclesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list: cycles, loading } = useSelector(s => s.cycles);
  const [yearFilter, setYearFilter] = useState('All');
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => { dispatch(fetchCycles()); }, [dispatch]);

  const years = [...new Set(cycles.map(c => {
    const m = c.cycle_id?.match(/FY(\d{2})-(\d{2})/);
    return m ? `20${m[1]}-20${m[2]}` : null;
  }).filter(Boolean))].sort().reverse();

  const filtered = yearFilter === 'All'
    ? cycles
    : cycles.filter(c => c.cycle_id?.includes(yearFilter.slice(2, 4)));

  const setLoading = (id, key, val) =>
    setActionLoading(prev => ({ ...prev, [`${id}_${key}`]: val }));

  const handleDownload = async (cycle) => {
    setLoading(cycle.id, 'dl', true);
    try {
      const { data } = await cyclesAPI.downloadConsolidated(cycle.id);
      downloadBlob(data, `${cycle.cycle_id}_recommendations.xlsx`);
    } catch { toast.error('Download failed.'); }
    finally { setLoading(cycle.id, 'dl', false); }
  };

  const handleComplete = async (cycle) => {
    if (!window.confirm(`Mark ${cycle.cycle_id} as complete?`)) return;
    setLoading(cycle.id, 'complete', true);
    try {
      await cyclesAPI.complete(cycle.id);
      dispatch(fetchCycles());
      toast.success('Cycle marked as complete.');
    } catch { toast.error('Action failed.'); }
    finally { setLoading(cycle.id, 'complete', false); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Increment Cycle Data</h1>
        <button className="btn btn-primary" onClick={() => navigate('/increment-cycles/new')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New Increment Cycle
        </button>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        {/* Filter bar */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--grey-100)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--grey-400)" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span style={{ fontSize: 13.5, color: 'var(--grey-500)', fontWeight: 500 }}>Assessment Year</span>
          <div className="select-wrap">
            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
              <option value="All">All</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}><span className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Increment ID</th>
                <th>SR – Date</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th style={{ textAlign: 'center' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--grey-400)' }}>No cycles found.</td></tr>
              ) : filtered.map(cycle => (
                <tr key={cycle.id}>
                  <td style={{ fontWeight: 600 }}>{cycle.cycle_id}</td>
                  <td>{formatDate(cycle.salary_revision_date)}</td>
                  <td>{formatDate(cycle.start_date)}</td>
                  <td>{formatDate(cycle.end_date)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      {/* View */}
                      <button
                        className="btn-icon magenta"
                        title="View Details"
                        onClick={() => navigate(`/increment-cycles/${cycle.id}`)}
                      >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                      </button>

                      {/* Download (only if has recommendations) */}
                      {cycle.step_status?.step4_done && (
                        <button
                          className="btn-icon magenta"
                          title="Download Report"
                          disabled={actionLoading[`${cycle.id}_dl`]}
                          onClick={() => handleDownload(cycle)}
                        >
                          {actionLoading[`${cycle.id}_dl`]
                            ? <span className="spinner" style={{ width: 14, height: 14 }} />
                            : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
                        </button>
                      )}

                      {/* Complete */}
                      {!cycle.is_complete && (
                        <button
                          className="btn-icon"
                          title="Mark Complete"
                          disabled={actionLoading[`${cycle.id}_complete`]}
                          onClick={() => handleComplete(cycle)}
                          style={{ color: 'var(--success)' }}
                        >
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                        </button>
                      )}
                      {cycle.is_complete && (
                        <span className="badge badge-success" style={{ fontSize: 11 }}>Complete</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
